import { useEffect } from "react"
import { RiSettings3Line } from "@remixicon/react"
import { useTranslation } from "react-i18next"

import BasicButton from "@/components/button/BasicButton"
import { useDashboardStore } from "@/stores/dashboard"
import { createGlobalStore } from "@/stores/global"

interface DashboardHeaderProps {
  onSave?: () => void
}

export default function DashboardHeader({ onSave }: DashboardHeaderProps) {
  const { t } = useTranslation()
  const isEditMode = useDashboardStore((s) => s.isEditMode)
  const setEditMode = useDashboardStore((s) => s.setEditMode)
  const saveSnapshot = useDashboardStore((s) => s.saveSnapshot)
  const restoreSnapshot = useDashboardStore((s) => s.restoreSnapshot)
  const clearSnapshot = useDashboardStore((s) => s.clearSnapshot)
  const resetToDefault = useDashboardStore((s) => s.resetToDefault)
  const setTrailingBreadcrumb = createGlobalStore((s) => s.setTrailingBreadcrumb)

  // edit 모드 진입 시 Container 의 BreadCrumbs 에 "Settings" 항목 trailing append.
  // 해제/언마운트 시 clear — Home → 다른 페이지 이동 후 잔여 노출 방지.
  useEffect(() => {
    if (isEditMode) {
      setTrailingBreadcrumb(t("button.settings", "Settings"))
    } else {
      setTrailingBreadcrumb(null)
    }

    return () => setTrailingBreadcrumb(null)
  }, [isEditMode, setTrailingBreadcrumb, t])

  const handleEnterEdit = () => {
    saveSnapshot()
    setEditMode(true)
  }

  const handleSave = () => {
    clearSnapshot()
    setEditMode(false)
    onSave?.()
  }

  const handleCancel = () => {
    restoreSnapshot()
  }

  const handleReset = () => {
    resetToDefault()
  }

  return (
    <div className={`DashboardHeader ${isEditMode ? "DashboardHeader--editing" : ""}`}>
      <div className="DashboardHeader__actions">
        {isEditMode ? (
          <>
            <BasicButton size="sm" type="ghost" onClick={handleReset}>
              {t("button.reset", "Reset")}
            </BasicButton>
            <BasicButton size="sm" type="default-outlined" onClick={handleCancel}>
              {t("button.cancel")}
            </BasicButton>
            <BasicButton size="sm" type="primary-contained" onClick={handleSave}>
              {t("button.save")}
            </BasicButton>
          </>
        ) : (
          <BasicButton size="sm" type="ghost" onClick={handleEnterEdit}>
            <RiSettings3Line size={16} />
            {t("button.settings", "Settings")}
          </BasicButton>
        )}
      </div>
    </div>
  )
}
